import { useEffect, useMemo, useState } from 'react';
import { Card, Loader } from './UI';
import { ACT, ACT_ORDER, getActivities, addActivity, updateActivity, deleteActivity, summarize } from '../lib/activities';
import { mskToday, addDays } from '../lib/alerts';
import { getRoutesBySv } from '../lib/data';
import StaffPlan from './StaffPlan';

const MONTHS = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const DOW = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];
const DOW_FULL = ['воскресенье','понедельник','вторник','среда','четверг','пятница','суббота'];

const dowOf = (iso) => new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10))).getUTCDay();
const pad = (n) => String(n).padStart(2, '0');
const monthStart = (y, m) => `${y}-${pad(m + 1)}-01`;
const monthEnd = (y, m) => addDays(monthStart(m === 11 ? y + 1 : y, (m + 1) % 12), -1);

function Chip({ a }) {
  const t = ACT[a.activity];
  if (!t) return null;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: t.bg, color: t.fg,
      border: t.dashed ? `1px dashed ${t.border}` : '1px solid transparent', whiteSpace: 'nowrap' }}>
      {t.short}{a.cnt ? ` ${a.cnt}` : ''}{a.source === 'file' ? '·ф' : ''}
    </span>
  );
}

// svId — чей календарь; readOnly — просмотр (ТМ/РМ); userId — кто правит
export default function SvPlanner({ svId, svIds, userId, readOnly = false, svName = '' }) {
  const today = mskToday();
  const [ym, setYm] = useState({ y: +today.slice(0, 4), m: +today.slice(5, 7) - 1 });
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(null);
  const [kind, setKind] = useState('plan');
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    let off = false;
    getRoutesBySv(svIds || svId).then(r => { if (!off) setRoutes(r || []); }).catch(() => {});
    return () => { off = true; };
  }, [svId, svIds]);

  const from = monthStart(ym.y, ym.m);
  const to = monthEnd(ym.y, ym.m);

  useEffect(() => {
    let off = false;
    setLoading(true); setErr('');
    getActivities([svId], from, to)
      .then(r => { if (!off) setActs(r); })
      .catch(e => { if (!off) setErr(e.message); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [svId, from, to]);

  const byDay = useMemo(() => {
    const m = {};
    acts.forEach(a => { (m[a.work_date] = m[a.work_date] || { plan: [], fact: [] })[a.kind].push(a); });
    return m;
  }, [acts]);

  const sum = useMemo(() => summarize(acts, from, to, to < today ? to : today), [acts, from, to, today]);

  // сетка: недели с понедельника
  const cells = useMemo(() => {
    const lead = (dowOf(from) + 6) % 7;
    const out = [];
    for (let i = -lead; ; i++) {
      const d = addDays(from, i);
      if (d > to && out.length % 7 === 0) break;
      out.push(d);
    }
    return out;
  }, [from, to]);

  function shift(n) {
    let y = ym.y, m = ym.m + n;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setYm({ y, m });
    setSel(null);
  }

  async function guard(fn) {
    setErr('');
    try { await fn(); } catch (e) { setErr(e.message); }
  }
  const add = (activity) => guard(async () => {
    const row = await addActivity({ svId, date: sel, kind, activity, userId });
    setActs(p => [...p, row]);
  });
  const patch = (a, p) => guard(async () => {
    const row = await updateActivity(a.id, p);
    setActs(prev => prev.map(x => x.id === a.id ? row : x));
  });
  const remove = (a) => guard(async () => {
    await deleteActivity(a.id);
    setActs(prev => prev.filter(x => x.id !== a.id));
  });

  const selDay = sel ? (byDay[sel] || { plan: [], fact: [] }) : null;
  const selList = selDay ? selDay[kind] : [];

  const stat = (label, val, sub, t) => (
    <div style={{ flex: '1 1 100px', background: t.bg, borderRadius: 10, padding: '8px 10px' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: t.fg }}>{val}</div>
      <div style={{ fontSize: 11, color: '#4A5568' }}>{label}</div>
      {sub ? <div style={{ fontSize: 10, color: '#5A6678' }}>{sub}</div> : null}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => shift(-1)} style={navBtn}>‹</button>
          <div style={{ fontSize: 13, fontWeight: 700, minWidth: 120, textAlign: 'center' }}>{MONTHS[ym.m]} {ym.y}</div>
          <button onClick={() => shift(1)} style={navBtn}>›</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {readOnly ? `Просмотр${svName ? ': ' + svName : ''}` : 'План вносите сами. Факт: вручную или из файла'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {stat('Перекрытия', sum.per, `визитов ${sum.perVisits}`, ACT.per)}
        {stat('Совтуры', sum.sov, 'за месяц', ACT.sov)}
        {stat('Стажировки', sum.st + sum.stm, `с визитами ${sum.st}`, ACT.st)}
        {stat('Аудиты', sum.auCnt || sum.au, 'за месяц', ACT.au)}
        {stat('Офисных дней', sum.of, '', ACT.of)}
        {stat('Аудит с АСМ', sum.asm, 'за месяц', ACT.asm)}
        {stat('Отпуск', sum.ot, 'дней', ACT.ot)}
        {stat('Больничный', sum.sick, 'дней', ACT.sick)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
        В итогах для дня берётся факт, если он есть, иначе план. Заполнено рабочих дней: <b>{sum.filled}</b> из {sum.workDays}.
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {ACT_ORDER.map(c => <Chip key={c} a={{ activity: c, source: 'manual' }} />)}
      </div>

      {err && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8 }}>{err}</div>}
      <Card style={{ padding: 8 }}>
        {loading ? <Loader /> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 3, marginBottom: 3 }}>
              {DOW.map((d, i) => <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: i >= 5 ? '#9C0006' : '#5A6678' }}>{d}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 3 }}>
              {cells.map((d, i) => {
                if (d < from || d > to) return <div key={d} style={{ minHeight: 84, borderRadius: 6, background: '#F7F9FC' }} />;
                const day = byDay[d] || { plan: [], fact: [] };
                const we = i % 7 >= 5;
                const isSel = d === sel;
                const isToday = d === today;
                const past = d < today;
                return (
                  <div key={d} onClick={() => setSel(d)} style={{
                    minHeight: 84, borderRadius: 6, cursor: 'pointer', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    background: we && !day.plan.length ? '#F2F4F7' : '#fff', border: '1px solid #E2E8F0',
                    outline: isSel ? '2px solid #2E75B6' : 'none', outlineOffset: -1,
                  }}>
                    <div style={{ padding: '3px 5px 1px' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: isToday ? '#fff' : '#4A5568',
                        background: isToday ? '#2E75B6' : 'transparent', borderRadius: 10, padding: isToday ? '0 6px' : 0 }}>{+d.slice(8)}</span>
                    </div>
                    <div style={{ padding: '0 3px', display: 'flex', flexWrap: 'wrap', gap: 2, flex: 1, alignContent: 'flex-start' }}>
                      {day.plan.map(a => <Chip key={a.id} a={a} />)}
                    </div>
                    <div style={{ padding: '2px 3px', borderTop: '1px dashed #CBD5E0', background: '#FAFBFC', minHeight: 22, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#A0AEC0' }}>Ф</span>
                      {day.fact.length ? day.fact.map(a => <Chip key={a.id} a={a} />)
                        : (past ? <span style={{ fontSize: 9, color: '#A0AEC0' }}>нет</span> : null)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
              Верх ячейки — план, строка «Ф» — факт. «·ф» — запись загружена из файла, она важнее ручной.
            </div>
          </>
        )}
      </Card>

      {sel && (
        <div onClick={() => setSel(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 300, overflowY: 'auto', padding: '24px 12px' }}>
        <div onClick={e => e.stopPropagation()} style={{ maxWidth: 560, margin: '0 auto' }}>
        <Card style={{ border: '2px solid var(--accent)', background: '#fff' }}>
          <div style={{ padding: '10px 14px', background: '#EBF4FF', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-dark)' }}>
              {sel.slice(8)}.{sel.slice(5, 7)}.{sel.slice(0, 4)}, {DOW_FULL[dowOf(sel)]}
            </div>
            <div style={{ display: 'inline-flex', border: '1px solid #BBD6EE', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
              {[['plan', 'План'], ['fact', 'Факт']].map(([k, l]) => (
                <button key={k} onClick={() => setKind(k)} style={{ padding: '5px 16px', fontSize: 12, fontWeight: kind === k ? 700 : 600, border: 'none',
                  background: kind === k ? '#2E75B6' : '#fff', color: kind === k ? '#fff' : '#5A6678' }}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {err && <div style={{ fontSize: 12, color: 'var(--red)' }}>{err}</div>}
            {selList.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{kind === 'plan' ? 'План на день не внесён.' : 'Факт не внесён.'}</div>}
            {selList.map(a => <ActRow key={a.id} a={a} routes={routes} readOnly={readOnly} onPatch={patch} onRemove={remove} />)}
            {!readOnly && (
              <>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Добавить в {kind === 'plan' ? 'план' : 'факт'}:</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {ACT_ORDER.map(c => {
                    const t = ACT[c];
                    return (
                      <button key={c} onClick={() => add(c)} style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: t.bg, color: t.fg,
                        border: t.dashed ? `1px dashed ${t.border}` : '1px solid transparent', cursor: 'pointer' }}>+ {t.label}</button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5, borderTop: '1px solid #EDF2F7', paddingTop: 8 }}>
                  В один день можно несколько активностей. «Стажировка МЕ» и «Стажировка МЕ−» — укажите ФИО кандидата. «Совтур» — маршрут и с кем едете. Правки сохраняются сразу, срока редактирования нет.
                </div>
              </>
            )}
          </div>
          <div style={{ padding: '0 14px 12px' }}>
            <button onClick={() => setSel(null)} style={{ width: '100%', padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#fff', background: 'var(--accent)', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Готово</button>
          </div>
        </Card>
        </div>
        </div>
      )}
      <div style={{ marginTop: 14 }}><StaffPlan routes={routes} svIds={svIds} svId={svId} userId={userId} readOnly={readOnly} /></div>
    </div>
  );
}

const navBtn = { width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)', background: '#fff', fontSize: 16, cursor: 'pointer' };
const inp = { border: '1px solid #CBD5E0', background: '#fff', borderRadius: 6, padding: '4px 8px', fontSize: 13, fontWeight: 600 };

const mrcOf = (r) => {
  const m = Array.isArray(r.merchandisers) ? r.merchandisers[0] : r.merchandisers;
  return (r.is_vacancy || !m?.full_name || /вакансия/i.test(m.full_name)) ? '' : m.full_name;
};
const routeLabel = (r) => `${r.is_pm ? 'ПМ · ' : ''}${r.internal_code} · ${mrcOf(r) || 'Вакансия'}`;

function ActRow({ a, routes = [], readOnly, onPatch, onRemove }) {
  const t = ACT[a.activity];
  const [cnt, setCnt] = useState(a.cnt ?? '');
  const [cand, setCand] = useState(a.candidate ?? '');
  useEffect(() => { setCnt(a.cnt ?? ''); setCand(a.candidate ?? ''); }, [a.cnt, a.candidate]);
  const missing = t.cand && !String(cand).trim();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#F7F9FC', border: '1px solid #E2E8F0', borderRadius: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: t.bg, color: t.fg,
        border: t.dashed ? `1px dashed ${t.border}` : '1px solid transparent', minWidth: 112, textAlign: 'center' }}>{t.label}</span>
      <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {a.activity === 'per' && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <select value={a.route_id || ''} disabled={readOnly || a.source === 'file'} style={{ ...inp, width: '100%', fontWeight: 500 }}
              onChange={e => onPatch(a, { route_id: e.target.value || null, cnt: null })}>
              <option value="">— выберите маршрут —</option>
              {routes.map(r => <option key={r.id} value={r.id}>{routeLabel(r)}</option>)}
              {a.route_id && !routes.some(r => r.id === a.route_id) && <option value={a.route_id}>(маршрут скрыт или не из вашего списка)</option>}
            </select>
            {a.route_id && (
              <div style={{ fontSize: 12, color: '#276221', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>Заказ: <b>{a.info?.order ?? '—'}</b></span>
                <span>План: <b>{a.info?.plan ?? '—'}</b></span>
                <span>Факт: <b>{a.info?.fact ?? 'ещё нет'}</b></span>
                {a.info?.status ? <span>Статус: <b>{a.info.status}</b></span> : null}
              </div>
            )}
            {a.route_id && <div style={{ fontSize: 10, color: '#5A6678' }}>Данные из Табеля, только чтение. В итоги «Перекрытий» идёт {a.kind === 'fact' ? 'факт' : 'план (или заказ)'}.</div>}
          </div>
        )}
        {a.activity === 'sov' && (() => {
          const names = [...new Set(routes.map(mrcOf).filter(Boolean))].sort((x, y) => x.localeCompare(y, 'ru'));
          const inList = !a.candidate || names.includes(a.candidate);
          return (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <select value={a.route_id || ''} disabled={readOnly || a.source === 'file'} style={{ ...inp, width: '100%', fontWeight: 500 }}
                onChange={e => {
                  const r = routes.find(x => x.id === e.target.value);
                  onPatch(a, { route_id: e.target.value || null, ...(r && !a.candidate && mrcOf(r) ? { candidate: mrcOf(r) } : {}) });
                }}>
                <option value="">— выберите маршрут —</option>
                {routes.map(r => <option key={r.id} value={r.id}>{routeLabel(r)}</option>)}
                {a.route_id && !routes.some(r => r.id === a.route_id) && <option value={a.route_id}>(маршрут скрыт или не из вашего списка)</option>}
              </select>
              <select value={inList ? (a.candidate || '') : '__other'} disabled={readOnly || a.source === 'file'} style={{ ...inp, width: '100%', fontWeight: 500 }}
                onChange={e => { if (e.target.value === '__other') onPatch(a, { candidate: ' ' }); else onPatch(a, { candidate: e.target.value || null }); }}>
                <option value="">— с кем поедет (ФИО) —</option>
                {names.map(n => <option key={n} value={n}>{n}</option>)}
                <option value="__other">Другой (ввести вручную)…</option>
              </select>
              {!inList && (
                <input value={cand} disabled={readOnly || a.source === 'file'} placeholder="ФИО вручную" style={{ ...inp, width: '100%' }}
                  onChange={e => setCand(e.target.value)}
                  onBlur={() => { if (cand !== (a.candidate ?? '')) onPatch(a, { candidate: cand.trim() || null }); }} />
              )}
            </div>
          );
        })()}
        {t.cnt && !(a.activity === 'per' && a.route_id) && (
          <>
            <span style={{ fontSize: 11, color: '#5A6678' }}>{t.cnt}</span>
            <input type="number" min="0" value={cnt} disabled={readOnly || a.source === 'file'} style={{ ...inp, width: 64 }}
              onChange={e => setCnt(e.target.value)}
              onBlur={() => { const v = cnt === '' ? null : Math.max(0, parseInt(cnt, 10) || 0); if (v !== (a.cnt ?? null)) onPatch(a, { cnt: v }); }} />
          </>
        )}
        {t.cand && (
          <>
            <span style={{ fontSize: 11, color: '#5A6678' }}>кандидат</span>
            <input value={cand} disabled={readOnly || a.source === 'file'} placeholder="ФИО кандидата"
              style={{ ...inp, width: 190, borderColor: missing && !readOnly ? '#E0A800' : '#CBD5E0' }}
              onChange={e => setCand(e.target.value)}
              onBlur={() => { if (cand !== (a.candidate ?? '')) onPatch(a, { candidate: cand.trim() || null }); }} />
          </>
        )}
        {a.source === 'file' && <span style={{ fontSize: 10, color: '#5A6678' }}>из файла</span>}
      </div>
      {!readOnly && a.source !== 'file' && (
        <button onClick={() => onRemove(a)} title="Удалить" style={{ border: 'none', background: 'none', fontSize: 18, color: '#A0AEC0', cursor: 'pointer', padding: '0 4px' }}>×</button>
      )}
    </div>
  );
}
